import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as popups from '../../../../constants/popup-types';
import * as routes from '../../../../constants/routes';
import { addPopup, getLegalAdviceQuestions } from '../../../../redux/actions';
import ButtonOutlined from '../../../../components/common/buttons/button_outlined';
import TopBanner from '../../sections/top_banner';
import CategorySlider from '../../../../components/common/sliders/category_slider';
import TagsSlider from '../../../../components/common/sliders/tags_slider';
import QuickSearch from '../../sections/quick_search';
import QuestionResultsList from '../../sections/question_results_list';
import './index.scss';
import ImageHeader from "../../../../components/image-header";
import mobileBg from 'assets/images/legal_advice_header-bg-mobile.jpg'
import {getMediaPath} from "../../../../app/functions";
import {useDeviceState} from "../../../../app/device_state";
import { useGetLegalAdviceQuestionsQuery } from '../../../../redux/api/relations.api';

const BaseRoute = ({legalAdvicePage}) => {
    const history = useHistory();
    const params = useParams();
    const dispatch = useDispatch();

    const categorySliderRef = useRef(null);
    const tagsSliderRef = useRef(null);

    const translations = useSelector((store) => store.config.Translations);
    const userData = useSelector((store) => store.config.UserData);

    const [filteredQuestionsByCategory, setFilteredQuestionsByCategory] = useState([]);
    const [filteredQuestionsByCategoryAndTags, setFilteredQuestionsByCategoryAndTags] = useState([]);
    const {isMobile} = useDeviceState()
    const [selectedCategory, setSelectedCategory] = useState(0);

    const [selectedTags, setSelectedTags] = useState([]);
    const [allAvailableTags, setAllAvailableTags] = useState([]);
    const [categoryAvailableTags, setCategoryAvailableTags] = useState([]);


    const { data: legalAdviceQuestionsData, error, isLoading } = useGetLegalAdviceQuestionsQuery();
    const [legalAdviceQuestions, setLegalAdviceQuestions] = useState(null);

    useEffect(() => {
        if (legalAdviceQuestionsData) {
            setLegalAdviceQuestions(legalAdviceQuestionsData);
        }
    }, [legalAdviceQuestionsData]);

    useEffect(() => {
        let paramsTag = params?.tag;

        if (paramsTag) {
          setSelectedTags([...selectedTags, +paramsTag]);
        }
      }, []);

    const availableCategories = [
        {
            id: 0,
            title: translations['legal_advice_all_categories'],
        },
        ...Object.values(legalAdvicePage.categories)
            .filter((categoryData) => {
                if (!legalAdviceQuestions) return;

                // Filter categories without questions
                return legalAdviceQuestions.some(
                    (questionData) => questionData.category_id === categoryData.id
                );
            })
            .sort((a, b) => {
                return a.order_num - b.order_num;
            }),
    ];

    const handleCategorySelect = (categoryId) => {
        setSelectedCategory(categoryId);
        setSelectedTags([]);

        // Reset tags slider to beginning
        if (tagsSliderRef.current) {
            tagsSliderRef.current.slickGoTo(999);
        }
    };

    const handleTagSelect = (tagId) => setSelectedTags(
        [...selectedTags, tagId]
    );

    const handleTagRemove = (tagId) => setSelectedTags(
        selectedTags.filter((savedTagId) => savedTagId !== tagId)
    );

    const handleTagClick = (tagId) => {
        setSelectedCategory(0);
        setSelectedTags([tagId]);
    };

    const handleNewQuestionClick = () => {
        if (userData) {
            history.push(routes.legal_advice + routes.legal_advice_routes.new_question);
        } else {
            dispatch(addPopup({
                type: popups.LOGIN,
                subtitleText: translations['legal_advice_login_required'],
                onLogin: () => {
                    setTimeout(() => {
                        history.push(routes.legal_advice + routes.legal_advice_routes.new_question)
                    }, 500);
                }
            }));
        }
    };



    // Filter the questions data according to selected category and tags
    useEffect(() => {
        if (!legalAdviceQuestions) return;

        // Show only questions from the selected category
        const newFilteredQuestionsByCategory = legalAdviceQuestions.filter((questionData) => {
            if (selectedCategory && selectedCategory !== questionData.category_id) {
                return false;
            }

            return true;
        });

        // Show only questions with the selected tags (at least one match, not necessarily all)
        const newFilteredQuestionsByCategoryAndTags = newFilteredQuestionsByCategory.filter((questionData) => {
            if (selectedTags.length > 0 && !selectedTags.some(
                (tagId) => questionData.linked_tags.includes(Number(tagId))
            )) {
                return false;
            }

            return true;
        });

        setFilteredQuestionsByCategory(newFilteredQuestionsByCategory);
        setFilteredQuestionsByCategoryAndTags(newFilteredQuestionsByCategoryAndTags);
    }, [legalAdviceQuestions, selectedCategory, selectedTags]);

    // Set all available tags according to questions data
    useEffect(() => {
        if (!legalAdviceQuestions) return;

        setAllAvailableTags(
            legalAdvicePage.tags.filter(
                (tagData) => legalAdviceQuestions.some(
                    (questionData) => questionData.linked_tags.includes(tagData.id)
                )
            )
        );
    }, [legalAdviceQuestions]);

    // Set category available tags according to filtered questions by category
    useEffect(() => {
        setCategoryAvailableTags(
            legalAdvicePage.tags.filter(
                (tagData) => filteredQuestionsByCategory.some(
                    (questionData) => questionData.linked_tags.includes(tagData.id)
                )
            )
        );
    }, [filteredQuestionsByCategory]);


    const titleBg = isMobile ? (legalAdvicePage.page.mobileCoverImage || mobileBg ) :legalAdvicePage.page.coverImage

    return legalAdviceQuestions && (
        <>
            <ImageHeader
                image={getMediaPath(titleBg)}
                title={legalAdvicePage.page.header_title}
                subtitle={legalAdvicePage.page.header_description}
            />
            <div className="legal-advice-base-route content-container content-container_narrow">
                <div className="categories-slider-container">
                    {availableCategories.length > 0 && (
                        <CategorySlider
                            selectedCategory={selectedCategory}
                            availableCategories={availableCategories}
                            onCategorySelect={handleCategorySelect}
                            onCategoryRemove={() => {}}
                            customSliderSettings={{
                                mobile: {
                                    slidesToShow: 4,
                                    slidesToScroll: 4,
                                },
                                tablet: {
                                    slidesToShow: 5,
                                    slidesToScroll: 5,
                                },
                                desktop: {
                                    slidesToShow: 6,
                                    slidesToScroll: 6,
                                },
                                desktopLarge: {
                                    slidesToShow: 7,
                                    slidesToScroll: 7,
                                },
                            }}
                            reinitializeOnDataChange={false}
                            ref={categorySliderRef}
                        />
                    )}
                </div>

                <div className="upper-row">
                    <div className="upper-row-quick-search">
                        <QuickSearch
                            availableTags={allAvailableTags}
                            onTagClick={handleTagClick}
                            onNewQuestionClick={handleNewQuestionClick}
                        />
                    </div>

                    <h4 className="upper-row-title">
                        {
                            selectedCategory
                                ? legalAdvicePage.categories[selectedCategory].title
                                : translations['legal_advice_all_categories']
                        }
                    </h4>

                    <ButtonOutlined
                        type="button"
                        className="upper-row-link"
                        onClick={handleNewQuestionClick}
                    >
                        + {legalAdvicePage.page.add_button_text}
                    </ButtonOutlined>
                </div>

                {filteredQuestionsByCategoryAndTags.length > 0 ? (
                    <QuestionResultsList legalAdvicePage={legalAdvicePage} data={filteredQuestionsByCategoryAndTags} />
                ) : (
                    <p className="filtered-nothing-found">
                        {translations['legal_advice_no_questions']}
                    </p>
                )}

                <div className="tags-slider-container">
                    {categoryAvailableTags.length > 0 && (
                        <TagsSlider
                            selectedTags={selectedTags}
                            availableTags={categoryAvailableTags}
                            onTagSelect={handleTagSelect}
                            onTagRemove={handleTagRemove}
                            customSliderSettings={{
                                mobile: {
                                    slidesToShow: 3,
                                    slidesToScroll: 3,
                                },
                                tablet: {
                                    slidesToShow: 3,
                                    slidesToScroll: 3,
                                },
                                desktop: {
                                    slidesToShow: 4,
                                    slidesToScroll: 4,
                                },
                                desktopLarge: {
                                    slidesToShow: 6,
                                    slidesToScroll: 6,
                                },
                            }}
                            reinitializeOnDataChange={false}
                            ref={tagsSliderRef}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default BaseRoute;
